/**
 * CHECK of the Publish workspace (W2-09 publish-import; DESIGN.md §4.16, §5.10): problem counts by severity, each
 * with a link to the Problems panel filtered on that severity (the blocking ones link to their list right here: it
 * also holds the export's own checks, which the Problems panel does not show), then the problems that block the
 * export and the most important others, each with "Go" (seek, select, make the line active) and its first fix.
 * Severities pair an icon with a word. While a checker pass runs, "Checking the chart..." says the counts may still
 * change.
 */

import { useRef } from 'react';
import type { AppServices, IconName } from '../../app/registry/types';
import type { Severity } from '../../core/check/types';
import { Button } from '../../ui/Button';
import { Icon } from '../../ui/Icon';
import { Section } from '../../ui/Section';
import type { ProblemsView, Text } from './hooks';
import { bySeverity, issueOfProblem, type PreflightIssue } from './preflight';
import { m } from './publish.messages';
import s from './Publish.module.css';

export const SEVERITY_ICONS: Readonly<Record<Severity, IconName>> = {
  fatal: 'OctagonX',
  error: 'CircleX',
  warning: 'TriangleAlert',
  tip: 'Lightbulb',
};

const SEVERITY_TEXT = {
  fatal: m.sevFatal,
  error: m.sevError,
  warning: m.sevWarning,
  tip: m.sevTip,
} as const;

/** How many problems the summary lists below the counts (the rest is in the Problems panel). */
export const TOP_PROBLEMS = 6;

/**
 * Opens the Problems panel (`problems.show`). `severities`: shows exactly the problems of these severities (every
 * category, the whole chart, no search text), so "View warnings" lists the warnings the summary counted.
 */
export function showProblems(services: AppServices, severities?: readonly Severity[]): void {
  void services.registry.run('problems.show', severities === undefined ? undefined : { severities }, 'button');
}

/** "Go": the chart workspace at the problem (seek, select, active line). */
export async function goToIssue(services: AppServices, issue: PreflightIssue): Promise<void> {
  if (issue.go === null) return;
  await services.registry.run('app.workspace', { workspace: 'chart' }, 'button');
  await services.registry.run('nav.reveal', issue.go, 'button');
}

/** Runs the first fix of a row: the checker applies its own fixes (undoable); local fixes are commands. */
export async function fixIssue(services: AppServices, issue: PreflightIssue): Promise<void> {
  if (issue.fix === null) return;
  if (issue.fromChecker) {
    const ok = await services.problems.applyFix(issue.id, 0);
    if (ok) return;
  }
  await services.registry.runAny(issue.fix.command, issue.fix.args, 'fix');
}

export function IssueRow(props: {
  readonly services: AppServices;
  readonly text: Text;
  readonly issue: PreflightIssue;
}) {
  const { services, text, issue } = props;
  const message = text.tr(issue.message);
  return (
    <li className={s.issue} data-severity={issue.severity} data-rule={issue.rule}>
      <Icon name={SEVERITY_ICONS[issue.severity]} size={16} />
      <div className={s.issueMain}>
        <span className={s.issueWord}>
          {text.t(m.severityWord, { severity: issue.severity })} · {issue.rule}
        </span>
        <span className={s.issueMessage}>{message}</span>
        {issue.why !== undefined && <span className={s.issueWhy}>{text.tr(issue.why)}</span>}
      </div>
      <div className={s.issueActions}>
        {issue.go !== null && (
          <Button
            size="sm"
            variant="ghost"
            icon="Crosshair"
            aria-label={text.t(m.goTo, { message })}
            onClick={() => {
              void goToIssue(services, issue);
            }}
          >
            {text.t(m.go)}
          </Button>
        )}
        {issue.fix !== null && (
          <Button
            size="sm"
            icon="Wrench"
            onClick={() => {
              void fixIssue(services, issue);
            }}
          >
            {text.tr(issue.fix.label)}
          </Button>
        )}
      </div>
    </li>
  );
}

export interface CheckSummaryProps {
  readonly services: AppServices;
  readonly text: Text;
  readonly problems: ProblemsView;
  /** Blocking problems (fatal checker problems plus the export's own checks). */
  readonly blocking: readonly PreflightIssue[];
}

export function CheckSummary(props: CheckSummaryProps) {
  const { services, text, problems, blocking } = props;
  const blockingRef = useRef<HTMLHeadingElement>(null);
  const counts: Readonly<Record<Severity, number>> = { ...problems.counts, fatal: blocking.length };
  const others = bySeverity(problems.problems.filter((p) => p.severity !== 'fatal'));
  const top = others.slice(0, TOP_PROBLEMS);
  const more = others.length - top.length;
  return (
    <Section
      title={text.t(m.checkTitle)}
      level={2}
      actions={
        <Button
          size="sm"
          variant="ghost"
          icon="RefreshCw"
          onClick={() => {
            services.problems.fullCheck();
          }}
        >
          {text.t(m.checkAgain)}
        </Button>
      }
    >
      <p className={s.checkStatus} role="status">
        {problems.checking && (
          <>
            <Icon name="LoaderCircle" size={14} />
            {text.t(m.checkingChart)}
          </>
        )}
      </p>
      <ul className={s.severityList} aria-label={text.t(m.checkTitle)}>
        {(['fatal', 'error', 'warning', 'tip'] as const).map((sev) => {
          const count = counts[sev];
          return (
            <li key={sev} className={s.severityRow} data-severity={sev} data-count={count === 0 ? 'zero' : 'nonzero'}>
              <Icon name={count === 0 ? 'CircleCheck' : SEVERITY_ICONS[sev]} size={16} />
              <span className={s.severityText}>{text.t(SEVERITY_TEXT[sev], { count })}</span>
              {count > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={sev === 'fatal' ? text.t(m.viewBlocking) : text.t(m.viewIn, { severity: sev })}
                  onClick={() => {
                    if (sev !== 'fatal') {
                      showProblems(services, [sev]);
                      return;
                    }
                    const heading = blockingRef.current;
                    if (heading === null) return;
                    if (typeof heading.scrollIntoView === 'function') heading.scrollIntoView({ block: 'start' });
                    heading.focus();
                  }}
                >
                  {text.t(m.view)}
                </Button>
              )}
            </li>
          );
        })}
      </ul>
      {blocking.length > 0 && (
        <>
          <h3 className={s.subTitle} ref={blockingRef} tabIndex={-1}>
            {text.t(m.blockingTitle)}
          </h3>
          <ul className={s.issueList} aria-label={text.t(m.blockingTitle)}>
            {blocking.map((issue) => (
              <IssueRow key={issue.id} services={services} text={text} issue={issue} />
            ))}
          </ul>
        </>
      )}
      {top.length > 0 && (
        <>
          <h3 className={s.subTitle}>{text.t(m.topTitle)}</h3>
          <ul className={s.issueList} aria-label={text.t(m.topTitle)}>
            {top.map((p) => (
              <IssueRow key={p.id} services={services} text={text} issue={issueOfProblem(p)} />
            ))}
          </ul>
          {more > 0 && (
            <Button
              size="sm"
              variant="ghost"
              icon="ListChecks"
              onClick={() => {
                showProblems(services, ['error', 'warning', 'tip']);
              }}
            >
              {text.t(m.moreInProblems, { count: more })}
            </Button>
          )}
        </>
      )}
    </Section>
  );
}
